import { NavLink } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

export interface AppHeaderProps {
  /** Short product name shown on the left, e.g. "Dhuttu Console". */
  title: string;
  /** Optional logo (path under /public, or null for no logo). */
  logoSrc?: string | null;
  /** Horizontal nav items. */
  nav: NavItem[];
  /** Right-side content slot — env switcher, user menu, etc. */
  right?: ReactNode;
}

/**
 * Top horizontal nav header. Modeled after Archer Console: white bar,
 * h-14, system font, primary-tinted active link.
 */
export function AppHeader({ title, logoSrc, nav, right }: AppHeaderProps) {
  return (
    <header className="border-b border-gray-200 bg-white">
      <div className="flex items-center h-14 px-6 gap-3">
        <div className="flex items-center gap-2 mr-6">
          {logoSrc && <img src={logoSrc} alt="" className="h-7 w-auto" />}
          <h1 className="text-base font-semibold text-ink whitespace-nowrap">{title}</h1>
        </div>
        <nav className="flex gap-1 flex-1 min-w-0 overflow-x-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-gray-600 hover:text-ink hover:bg-gray-50'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        {right && <div className="flex items-center gap-2">{right}</div>}
      </div>
    </header>
  );
}

export interface UserAvatarMenuProps {
  name?: string | null;
  email?: string | null;
  onSignOut: () => void;
}

export function UserAvatarMenu({ name, email, onSignOut }: UserAvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const initials = ((name ?? email ?? '?').trim()
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('') || '?').toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
        title={email ?? name ?? 'Account'}
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100">
            {name && <div className="text-sm font-medium text-ink truncate">{name}</div>}
            {email && <div className="text-xs text-gray-500 truncate">{email}</div>}
            {!name && !email && <div className="text-sm text-gray-500">Signed in</div>}
          </div>
          <button
            onClick={onSignOut}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
